import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { verifyOfferOtp, resendOfferCode } from "../../api/api-hook";
import { saveOffersSession } from "./offersSession";

type VerifyState = {
  contactId?: string;
  lastName: string;
  memberNumber: string;
  codeReused?: boolean;
  emailed?: boolean;
  sentAt?: string | null;
  expiresAt?: string | null;
};

const formatCodeTime = (iso?: string | null) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const OffersVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as Partial<VerifyState>;

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The code was right but there's no current membership on file: offer to buy one instead of resending
  const [noMembership, setNoMembership] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // "new": a new code was just emailed. "earlier": the code emailed before is still valid, so nothing was
  // sent this time. "resent": Resend emailed the same code again. "resentNew": Resend emailed a new code.
  const [notice, setNotice] = useState<{ kind: "new" | "earlier" | "resent" | "resentNew"; sentAt: string; expiresAt: string }>({
    kind: state.codeReused && state.emailed === false ? "earlier" : "new",
    sentAt: formatCodeTime(state.sentAt),
    expiresAt: formatCodeTime(state.expiresAt),
  });

  // ✅ If user refreshes this page, router state is lost
  useEffect(() => {
    if (!state?.lastName || !state?.memberNumber) {
      navigate("/offers", { replace: true }); // <-- your offers lookup/login page
    }
  }, [state, navigate]);

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNoMembership(false);

    if (!/^\d{6}$/.test(code)) {
      setError("Enter a 6-digit code.");
      return;
    }

    setLoading(true);
    try {
      const data = await verifyOfferOtp(state.lastName!, state.memberNumber!, code);

      const offer_response  = JSON.parse(data || "");  

    console.log(offer_response)


      if (!offer_response?.success || offer_response.status !== "authenticated") {
        setError(offer_response?.message || "Invalid or expired code.");
        setNoMembership(offer_response?.error === "no_active_membership");
        return;
      }

      // ✅ OTP validated — stay logged in on this device, then move to offers homepage
      saveOffersSession(offer_response.contactId);
      navigate("/offers", {
        replace: true,
        state: { contactId: offer_response.contactId },
      });
    } catch (err) {
      console.error(err);
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }

    
  };

  const handleResend = async () => {
    setError(null);
    setResendLoading(true);
    try {
      const resp = await resendOfferCode(state.lastName!, state.memberNumber!);
      const data = JSON.parse(resp || "{}");
      if (data?.success) {
        setNotice({ kind: data.reused ? "resent" : "resentNew", sentAt: formatCodeTime(data.sentAt), expiresAt: formatCodeTime(data.expiresAt) });
        if (!data.reused) setCode("");
        setResendCooldown(30);
      } else {
        setError(data?.message || "Could not resend code. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const canSubmit = /^\d{6}$/.test(code);

  return (
    <section className="container my-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h2
                className="mb-3 text-center text-uppercase"
                style={{ fontSize: "1rem", letterSpacing: "0.08em" }}
              >
                VSGA GOLF CARD PORTAL
              </h2>

              <p className="text-muted text-center mb-4">
                {notice.kind === "earlier" ? (
                  <>
                    A code was already sent to your email on file{notice.sentAt && <> on {notice.sentAt}</>}
                    {notice.expiresAt && <> and works until {notice.expiresAt}</>}. Enter it below.
                    If you can't find that email, use <strong>Resend code</strong> below.
                  </>
                ) : (
                  "We've emailed a 6-digit code to the address on your VIP account. Enter it below."
                )}
              </p>

              <form onSubmit={verifyOtp}>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="form-control"
                  placeholder="Enter your OTP"
                />

                {notice.kind !== "earlier" && (
                  <div className="d-flex align-items-start gap-2 small text-muted mt-2">
                    <i className="bi bi-info-circle-fill flex-shrink-0" style={{ color: "#C9A24D", marginTop: 2 }} aria-hidden="true" />
                    <span>It can take a few minutes to arrive. If you don't see it, check your spam or junk folder.</span>
                  </div>
                )}

                <div className="text-center mt-3">
                  {notice.kind === "resentNew" && (
                    <div className="text-success small mb-1">A new code has been sent. Check your spam or junk folder if it doesn't arrive.</div>
                  )}
                  {notice.kind === "resent" && (
                    <div className="text-success small mb-1">
                      Your code has been sent again{notice.sentAt && <> ({notice.sentAt})</>}. It's the same code as before. Check your spam or junk folder if it doesn't arrive.
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0 small"
                    onClick={handleResend}
                    disabled={resendLoading || resendCooldown > 0 || loading}
                  >
                    {resendLoading
                      ? "Sending..."
                      : resendCooldown > 0
                      ? `Resend code (${resendCooldown}s)`
                      : "Resend code"}
                  </button>
                </div>

                <div className="col-12 d-flex justify-content-between mt-4">
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none px-0"
                    onClick={() => navigate("/offers", { replace: true })}
                    disabled={loading}
                  >
                    ← Back
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={loading || !canSubmit}
                  >
                    {loading ? "Verifying..." : "Next"}
                  </button>
                </div>

                {error && <div className="text-danger mt-2">{error}</div>}
                {noMembership && (
                  <div className="text-center mt-3">
                    <button type="button" className="btn btn-primary px-4" onClick={() => navigate("/step1")}>
                      Purchase a VIP Card
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OffersVerify;
